import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Container, Row, Col, Card } from 'react-bootstrap';
import { Alert } from 'react-bootstrap';
import api from '../../services/api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import PageHeader from '../../components/PageHeader';
import { FaPrint, FaDownload, FaArrowLeft } from 'react-icons/fa';
import { baseURL } from '../../services/api';

const PayrollSummary = () => {
  const [searchParams] = useSearchParams();
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };
  
  const [month, setMonth] = useState(searchParams.get('month') || getCurrentMonth());
  const [departments, setDepartments] = useState([]);
  const [processedSummary, setProcessedSummary] = useState([]);

  // Alert state
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');

  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const navigate = useNavigate();

  // Extract unique departments from the processed summary data
  useEffect(() => {
    if (processedSummary.length > 0) {
      const uniqueDepts = [];
      const deptMap = new Map();
      
      processedSummary.forEach(item => {
        if (item.department && !deptMap.has(item.department.id)) {
          deptMap.set(item.department.id, true);
          uniqueDepts.push({
            id: item.department.id,
            name: item.department.name
          });
        }
      });
      
      console.log('Extracted departments:', uniqueDepts);
      setDepartments(uniqueDepts);
    }
  }, [processedSummary]);

  useEffect(() => {
    // Fetch summary when month changes
    if (month) {
      fetchSummary();
    }
  }, [month]);

  const fetchSummary = () => {
    api.get(`/payrolls/monthly-summary?month=${month}`)
      .then(res => {
        console.log('API Response:', res);
        console.log('API Data:', JSON.stringify(res.data, null, 2));
        
        // Check if API response has success flag
        if (!res.data.success) {
          console.error('API Error:', res.data.message);
          if (res.data.message) {
            setAlertMessage(res.data.message);
            setAlertVariant('danger');
            setShowAlert(true);
          }
          setProcessedSummary([]);
          return;
        }

        // Get the data from the response
        const data = res.data.data;
        
        // If single object instead of array
        if (!Array.isArray(data)) {
          data = [data];
          console.log('Converted single object to array:', data);
        }

        // Log first item structure
        if (data.length > 0) {
          console.log('First item structure:', JSON.stringify(data[0], null, 2));
        }

        // Set the processed summary with the data
        setProcessedSummary(data);
        setAlertMessage('Monthly summary retrieved successfully');
        setAlertVariant('success');
        setShowAlert(true);
      })
      .catch(err => {
        console.error('Error fetching monthly summary:', err.response ? err.response.data : err);
        const errorMessage = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Error fetching monthly summary';
        setAlertMessage(errorMessage);
        setAlertVariant('danger');
        setShowAlert(true);
        setProcessedSummary([]);
      });
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
  };

  // Use processedSummary directly for filtering
  const filteredSummary = selectedDepartment === 'all' 
    ? processedSummary 
    : processedSummary.filter(item => {
        // If department is not an object, return false
        if (!item.department || typeof item.department !== 'object') return false;
        
        // Check if department matches the selected one
        return item.department.id === selectedDepartment || 
               item.department.name === selectedDepartment;
      });

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
  };

  const handleProcessPayroll = (employeeId) => {
    navigate(`/payroll/${employeeId}/${month}`);
  };
  const handleDownloadPayslip = (employeeId, month) => {
    // Open the download URL in a new tab
    const downloadUrl = `${baseURL}api/payslips/${employeeId}/${month}/download`;
    window.open(downloadUrl, '_blank');
  };
console.log(filteredSummary)
  return (
    <Layout>
      <div className="d-print-none">
        <TopBar />
      </div>
      
      <Container fluid className="d-print-none">
        <Card className="mb-2 mt-2">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Select Month</Form.Label>
                  <Form.Control
                    type="month"
                    value={month}
                    onChange={handleMonthChange}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Filter by Department</Form.Label>
                  <div className="d-flex align-items-center">
                 
                    <Form.Select 
                      value={selectedDepartment} 
                      onChange={handleDepartmentChange}
                      disabled={departments.length === 0}
                      style={{ maxWidth: 250 }}
                    >
                      <option value="all">All Departments</option>
                      {departments.length > 0 ? (
                        departments.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name || 'No Department'}
                          </option>
                        ))
                      ) : (
                        <option disabled>No departments found</option>
                      )}
                    </Form.Select>
                  </div>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex justify-content-end align-items-end">
                <Button variant="outline-primary" onClick={() => window.print()}>
                  <FaPrint className="me-2" /> Print
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      <Container fluid className="mt-2" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        {month && (
          <Card>
            <Card.Body>
              <Table striped bordered hover responsive className="d-print-none">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Employee Code</th>
                    <th>Leaves</th>
                    <th>Base Salary</th>
                    <th>Gross Salary</th>
                    <th>Total Allowance</th>
                    <th>Total Deductions</th>
                    <th>Net Salary</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSummary.map((item, index) => (
                    <tr key={item.employee_id || index}>
                      <td>{index + 1}</td>
                      <td>{item.employee_name}</td>
                      <td>{item.emp_code}</td>
                      <td>{item.leaves || 0}</td>
                      <td>{item.base_salary || 0}</td>
                      <td>{item.gross_salary || 0}</td>
                      <td>{item.total_allowance || 0}</td>
                      <td>{item.total_deductions || 0}</td>
                      <td>{item.net_salary || 0}</td>
                      <td>
                        <Button
                          size="sm"
                          variant={item.status === 'calculated' ? 'success' : 'primary'}
                          onClick={() => !item.status && handleProcessPayroll(item.employee_id)}
                        >
                          {item.status === 'calculated' ? 'Calculated' : 'Calculate Payroll'}
                        </Button>
                      </td>
                      {item.status === 'calculated' &&
                        <td className="actions-column">
                          <Button 
                            variant="outline-success" 
                            size="sm" 
                            className="ms-2 download-btn"
                            title="Download Payslip"
                            onClick={() => handleDownloadPayslip(item.employee_id, month)}
                          >
                            <FaDownload size={14} />
                          </Button>
                        </td>
                      }
                    </tr>
                  ))}
                </tbody>
              </Table>
              {/* Print-only table */}
              <div className="d-none d-print-block">
                <h4 className="mb-3">Payroll Summary ({month})</h4>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee</th>
                      <th>Employee Code</th>
                      <th>Net Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSummary.map((item, index) => (
                      <tr key={item.employee_id || index}>
                        <td>{index + 1}</td>
                        <td>{item.employee_name}</td>
                        <td>{item.emp_code}</td>
                        <td>{item.net_salary || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <style>{`
                @media print {
                  .d-print-none { display: none !important; }
                  .d-print-block { display: block !important; }
                }
              `}</style>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default PayrollSummary;
